/**
 * Système de coordonnées hexagonales axiales (q, r).
 *
 * Convention « pointy-top » : les hexagones ont une pointe en haut.
 * Voir https://www.redblobgames.com/grids/hexagons/ pour le détail.
 *
 *   - q : axe horizontal (colonnes en biais)
 *   - r : axe diagonal (rangées descendantes)
 *   - s = -q -r (3e coord, implicite)
 *
 * Toutes les fonctions sont pures et utilisables côté serveur comme client.
 */

export interface HexCoord {
  q: number;
  r: number;
}

/** Encode une coordonnée en string stable utilisable comme id de case. */
export function hexKey(q: number, r: number): string {
  return `${q},${r}`;
}

/** Decode une clé hexKey vers coordonnées. */
export function parseHexKey(key: string): HexCoord | null {
  const m = key.match(/^(-?\d+),(-?\d+)$/);
  if (!m) return null;
  return { q: Number(m[1]), r: Number(m[2]) };
}

/** Égalité de deux coordonnées. */
export function hexEquals(a: HexCoord, b: HexCoord): boolean {
  return a.q === b.q && a.r === b.r;
}

/** Les 6 directions (pointy-top). */
export const HEX_DIRECTIONS: ReadonlyArray<HexCoord> = [
  { q: +1, r: 0 },
  { q: +1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: +1 },
  { q: 0, r: +1 },
];

/** Renvoie les 6 cases adjacentes à (q, r). */
export function getAdjacent(q: number, r: number): HexCoord[] {
  return HEX_DIRECTIONS.map((d) => ({ q: q + d.q, r: r + d.r }));
}

/** Distance hex (norme L1 axiale) entre deux cases. */
export function hexDistance(a: HexCoord, b: HexCoord): number {
  return (Math.abs(a.q - b.q) + Math.abs(a.q + a.r - b.q - b.r) + Math.abs(a.r - b.r)) / 2;
}

/** Génère toutes les coordonnées d'une grille hexagonale de rayon `radius` autour de (0, 0). */
export function generateHexGrid(radius: number): HexCoord[] {
  const out: HexCoord[] = [];
  for (let q = -radius; q <= radius; q++) {
    const rMin = Math.max(-radius, -q - radius);
    const rMax = Math.min(radius, -q + radius);
    for (let r = rMin; r <= rMax; r++) {
      out.push({ q, r });
    }
  }
  return out;
}

/** Nombre total de cases dans une grille hexagonale de rayon `radius`. */
export function gridSize(radius: number): number {
  return 3 * radius * radius + 3 * radius + 1;
}

/**
 * Convertit (q, r) axial en (x, y) pixel pour le rendu SVG.
 * `size` est le rayon d'un hex en pixels (distance centre → coin).
 * Pointy-top layout.
 */
export function axialToPixel(q: number, r: number, size: number): { x: number; y: number } {
  const x = size * Math.sqrt(3) * (q + r / 2);
  const y = size * 1.5 * r;
  return { x, y };
}

/**
 * Renvoie N cases équidistantes sur l'anneau le plus extérieur (rayon = radius).
 * Utile pour placer les capitales équitablement.
 *
 * Si `n` > nombre de cases sur l'anneau, on prend toutes les cases dispo.
 */
export function pickEquidistantOnRing(radius: number, n: number): HexCoord[] {
  if (radius <= 0 || n <= 0) return [];
  // Construit l'anneau : on part de (-radius, +radius) et on parcourt les 6 côtés
  const ring: HexCoord[] = [];
  let q = -radius;
  let r = +radius;
  // Parcours dans le sens horaire : on suit les 6 directions
  for (let side = 0; side < 6; side++) {
    const dir = HEX_DIRECTIONS[side]!;
    for (let step = 0; step < radius; step++) {
      ring.push({ q, r });
      q += dir.q;
      r += dir.r;
    }
  }
  if (ring.length === 0) return [];
  // Sample N positions également espacées dans l'anneau
  const result: HexCoord[] = [];
  for (let i = 0; i < Math.min(n, ring.length); i++) {
    const idx = Math.floor((i * ring.length) / n) % ring.length;
    result.push(ring[idx]!);
  }
  return result;
}

/** Validation : (q, r) appartient à la grille de rayon `radius` ? */
export function isInGrid(q: number, r: number, radius: number): boolean {
  return Math.abs(q) <= radius && Math.abs(r) <= radius && Math.abs(q + r) <= radius;
}
